/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import React, {FunctionComponent, useMemo} from 'react';
import {makeStyles} from '@material-ui/core';
import classnames from 'classnames';
import {translate} from '@ion/core/src/locale/locale';
import Tooltip from '@ion/dls/src/components/tooltip/Tooltip';
import {SeatbeltBuckledIcon} from '@ion/dls/src/icons/SeatbeltBuckledIcon';
import {SeatbeltErrorIcon} from '@ion/dls/src/icons/SeatbeltErrorIcon';
import {SeatbeltParkedIcon} from '@ion/dls/src/icons/SeatbeltParkedIcon';
import {SeatbeltUnbuckledIcon} from '@ion/dls/src/icons/SeatbeltUnbuckledIcon';
import {Color, IconProps, IconSize} from '../../types/types';
import {SeatbeltStatus, validateSeatbeltStatus} from './seatbeltUtils';

interface StyleProps {
  spacing?: number;
}

export interface SeatbeltIconProps extends StyleProps {
  status?: SeatbeltStatus | string;
  color?: Color;
  size?: IconSize | number;
  showTooltip?: boolean;
  classNames?: string[];
}

const useStyles = makeStyles(() => ({
  container : {
    display    : 'inline-flex',
    alignItems : 'center',
    margin     : ({spacing}: StyleProps) => `0 ${spacing}px`
  }
}));

/**
 * Provides the mappings between the seatbelt status and its respective icon.
 * SeatbeltStatus.NotAvailable has no icon, it is rendered as text instead.
 */
const seatbeltIconMap: Partial<Record<SeatbeltStatus, FunctionComponent<IconProps>>> = {
  [SeatbeltStatus.Buckled]   : SeatbeltBuckledIcon,
  [SeatbeltStatus.Unbuckled] : SeatbeltUnbuckledIcon,
  [SeatbeltStatus.Error]     : SeatbeltErrorIcon,
  [SeatbeltStatus.Parked]    : SeatbeltParkedIcon
};

/**
 * @SeatbeltIcon
 *
 * The main purpose of the SeatbeltIcon is to provide a map from the seatbelt
 * status that we may receive from the CVS (received as a prop here) and map it
 * to the appropriate svg Seatbelt Icon component.
 *
 * The color prop is drilled down to the underlying icon and is intended to be
 * decided by the consumer, based on the background the icon is placed on, e.g.
 * white over a dark background. When it is omitted, the icons fall back to the
 * DLS default icon color, see dlsIconColor.
 * The status badge outline and glyph, e.g. the check mark, are always white
 * regardless of the color prop, as defined by design.
 *
 * The horizontal spacing is also decided by the consumer, as the icon may sit
 * next to other indicators, see dlsLayout('spacing') for the DLS spacing unit.
 * Use spacing for a symmetric horizontal margin, and classNames whenever the
 * consumer needs to define the spacing itself, e.g. an asymmetric margin.
 *
 * Icons display a tooltip with the status label on hover, e.g. 'Safe', use
 * showTooltip to opt out. The N/A text does not display a tooltip.
 *
 * ```typescript
 *
 *    import {SeatbeltIcon} from '@ion/dls/src/components/seatbeltIcon/SeatbeltIcon';
 *    import {SeatbeltStatus} from '@ion/dls/src/components/seatbeltIcon/seatbeltUtils';
 *
 *    <SeatbeltIcon status={SeatbeltStatus.Buckled} />
 *    <SeatbeltIcon status={seatbeltStatus} color={dlsColor('grey3')} />
 *    <SeatbeltIcon status={seatbeltStatus} size={IconSize.large} />
 *    <SeatbeltIcon status={seatbeltStatus} spacing={dlsLayout('spacing')} />
 *    <SeatbeltIcon status={seatbeltStatus} showTooltip={false} />
 *    <SeatbeltIcon status={seatbeltStatus} classNames={[styles.seatbeltIcon]} />
 *
 * ```
 * @param status - seatbelt status, unsupported values render nothing.
 * @param color - fill color drilled down to the icon figure lines.
 * @param size - icon size, defaults to IconSize.medium.
 * @param spacing - horizontal margin in px applied on both sides, defaults to 0.
 * @param showTooltip - displays the status label on hover, defaults to true.
 * @param classNames - additional classes applied to the icon container.
 */
export const SeatbeltIcon = ({
  status,
  color,
  size = IconSize.medium,
  spacing = 0,
  showTooltip = true,
  classNames = []
}: SeatbeltIconProps) => {
  const classes        = useStyles({spacing});
  const seatbeltStatus = useMemo(() => validateSeatbeltStatus(status), [status]);

  if (!seatbeltStatus) {
    return null;
  }

  const containerClassName = classnames(classes.container, ...classNames);
  const Icon = seatbeltIconMap[seatbeltStatus];
  if (!Icon) {
    return (
      <span className={containerClassName}>
        {translate('common:notAvailable')}
      </span>
    );
  }

  return (
    <Tooltip title={translate(`common:seatbelt.${seatbeltStatus}`)}
      placement='bottom'
      minWidth={0}
      renderOnlyChildren={!showTooltip}
      arrow>
      <span className={containerClassName} data-testid={`seatbelt-icon-${seatbeltStatus}`}>
        <Icon color={color} size={size as IconSize} />
      </span>
    </Tooltip>
  );
};

export default SeatbeltIcon;
